import {
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useMemo, useState } from 'react'
import { useTrips, useUpdateTripStatus, useVehicles } from '@/api/hooks'
import type { Vehicle } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { VehicleDialog } from '@/pages/logistics/VehicleDialog'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatDateTime,
  formatMoney,
  humanise,
  isoDate,
  PageHeader,
  StatusChip,
} from '@/components/common'

const NEXT_TRIP_STATUS: Record<string, string> = {
  PLANNED: 'VEHICLE_ASSIGNED',
  VEHICLE_ASSIGNED: 'LOADING',
  LOADING: 'DISPATCHED',
  DISPATCHED: 'ARRIVED',
  ARRIVED: 'UNLOADED',
  UNLOADED: 'RETURNING',
  RETURNING: 'COMPLETED',
}

export function LogisticsPage() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const [from, setFrom] = useState(() => isoDate(0))
  const [to, setTo] = useState(() => isoDate(0))

  const fromIso = useMemo(() => new Date(`${from}T00:00:00`).toISOString(), [from])
  const toIso = useMemo(() => new Date(`${to}T23:59:59`).toISOString(), [to])

  const { data: vehicles } = useVehicles({})
  const [addingVehicle, setAddingVehicle] = useState(false)
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null)
  const { data: trips, isLoading, error } = useTrips({ from: fromIso, to: toIso })
  const updateStatus = useUpdateTripStatus()

  const expiringPapers = vehicles?.filter((vehicle) => !vehicle.roadLegal) ?? []

  return (
    <>
      <PageHeader
        title="Logistics"
        subtitle="Vehicles, trips and proof of delivery"
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              label="From"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        }
      />

      <ErrorBanner error={error ?? updateStatus.error} />

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Card sx={{ height: '100%' }}>
            {isLoading && <LinearProgress />}
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Trips
              </Typography>
              {!trips || trips.length === 0 ? (
                <EmptyState
                  message="No trips scheduled in this window."
                  hint="Trips are planned against outdoor catering and bulk takeaway orders."
                />
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Trip</TableCell>
                      <TableCell>Vehicle</TableCell>
                      <TableCell>Route</TableCell>
                      <TableCell>Dispatch</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trips.map((trip) => (
                      <TableRow key={trip.id} hover>
                        <TableCell>
                          <Typography variant="body2">{trip.tripNumber}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {trip.foodContainerCount} container(s)
                          </Typography>
                        </TableCell>
                        <TableCell>{trip.registrationNumber ?? 'Unassigned'}</TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            {trip.pickupLocation ?? '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            → {trip.deliveryLocation ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDateTime(trip.dispatchAt)}</TableCell>
                        <TableCell>
                          <StatusChip status={trip.status} />
                        </TableCell>
                        <TableCell align="right">
                          {canWrite('logistics') && NEXT_TRIP_STATUS[trip.status] && (
                            <Button
                              size="small"
                              onClick={() =>
                                updateStatus.mutate({
                                  id: trip.id,
                                  body: { status: NEXT_TRIP_STATUS[trip.status] },
                                })
                              }
                            >
                              {humanise(NEXT_TRIP_STATUS[trip.status])}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          {expiringPapers.length > 0 && (
            <Card sx={{ mb: 2, borderColor: 'error.light' }}>
              <CardContent>
                <Typography variant="subtitle1" color="error.main" gutterBottom>
                  Vehicles that cannot be dispatched
                </Typography>
                <Stack spacing={1}>
                  {expiringPapers.map((vehicle) => (
                    <Typography key={vehicle.id} variant="body2">
                      {vehicle.registrationNumber} — insurance {formatDate(vehicle.insuranceExpiry)}, fitness{' '}
                      {formatDate(vehicle.fitnessExpiry)}
                    </Typography>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle1">Fleet</Typography>
                {/* The register is kept here, not typed afresh on every
                    outpass: a van has papers, a capacity and a usual driver,
                    and none of that belongs in a box on a gate pass. */}
                {canWrite('logistics') ? (
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setAddingVehicle(true)}>
                    Add vehicle
                  </Button>
                ) : null}
              </Stack>
              {!vehicles || vehicles.length === 0 ? (
                <EmptyState message="No vehicles on the register yet." />
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Registration</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Ownership</TableCell>
                      <TableCell align="right">Per trip</TableCell>
                      <TableCell>Papers</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vehicles.map((vehicle) => (
                      <TableRow key={vehicle.id} hover>
                        <TableCell>{vehicle.registrationNumber}</TableCell>
                        <TableCell>{humanise(vehicle.vehicleType)}</TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" label={humanise(vehicle.ownership)} />
                        </TableCell>
                        <TableCell align="right">{formatMoney(vehicle.costPerTrip)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={vehicle.roadLegal ? 'success' : 'error'}
                            label={vehicle.roadLegal ? 'Valid' : 'Expired'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {canWrite('logistics') ? (
                            <Tooltip title="Change this vehicle">
                              <IconButton
                                size="small"
                                aria-label={`Edit ${vehicle.registrationNumber}`}
                                onClick={() => setEditingVehicle(vehicle)}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <VehicleDialog open={addingVehicle} onClose={() => setAddingVehicle(false)} />
      <VehicleDialog
        open={editingVehicle !== null}
        vehicle={editingVehicle}
        onClose={() => setEditingVehicle(null)}
      />
    </>
  )
}
